import styled from 'styled-components';
import type {TicketPriority} from '../types/ticket';
import type {TicketingTokens} from '../../../styles/tokens';

type ColorToken = keyof TicketingTokens['color'];

const BADGE_BG: Record<TicketPriority, ColorToken> = {
	p0: 'criticalBg',
	p1: 'highBg',
	p2: 'mediumBg',
	p3: 'lowBg',
};
const BADGE_TEXT: Record<TicketPriority, ColorToken> = {
	p0: 'criticalText',
	p1: 'highText',
	p2: 'mediumText',
	p3: 'lowText',
};
const DOT_COLOR: Record<TicketPriority, string> = {
	p0: '#ef4444',
	p1: '#fb923c',
	p2: '#facc15',
	p3: '#94a3b8',
};
const LABEL: Record<TicketPriority, string> = {
	p0: 'Critical',
	p1: 'High',
	p2: 'Medium',
	p3: 'Low',
};

const Badge = styled.span<{$priority: TicketPriority}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 2px 10px;
	border-radius: ${p => p.theme.ticketing.radius.xs};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => p.theme.ticketing.color[BADGE_BG[p.$priority]]};
	color: ${p => p.theme.ticketing.color[BADGE_TEXT[p.$priority]]};
`;

const Dot = styled.span<{$priority: TicketPriority}>`
	width: 8px;
	height: 8px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: ${p => DOT_COLOR[p.$priority]};
`;

const UrgencyIconSvg = styled.svg`
	width: 16px;
	height: 16px;
	color: #dc2626;
`;

interface Props {
	priority: TicketPriority;
}

function UrgencyIcon() {
	return (
		<UrgencyIconSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" role="img">
			<title>Urgent</title>
			<path
				fillRule="evenodd"
				d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
				clipRule="evenodd"
			/>
		</UrgencyIconSvg>
	);
}

export function PriorityBadge({priority}: Props) {
	const label = LABEL[priority];
	return (
		<Badge $priority={priority} aria-label={`Priority: ${label}`}>
			{priority === 'p0' && <UrgencyIcon />}
			<Dot $priority={priority} aria-hidden="true" />
			<span>{label}</span>
		</Badge>
	);
}
